<template>
  <div ref="container" class="sf-virtual-table__container">
    <div
      class="sf-virtual-table__header tw-bg-sf-snow-light"
      data-testid="sf-virtual-table-header"
      :style="headerStyle"
    >
      <div v-if="withSelector" class="tw-flex tw-justify-end tw-bg-slate-50">
        <sf-checkbox v-model="writableSelect"></sf-checkbox>
      </div>
      <slot name="header" />
    </div>
    <DynamicScroller
      class="sf-full-width rs-companies-list-table sf-virtual-table__scroller"
      :items="items"
      :min-item-size="itemSize"
      :size-dependencies="sizeDependencies"
      :key-field="keyField"
      @resize="getPaddingOffset"
    >
      <template #default="{ item, active }">
        <div class="sf-virtual-table__row">
          <slot name="default" :item="item" :active="active" />
        </div>
      </template>
    </DynamicScroller>
  </div>
</template>

<script>
import { DynamicScroller } from 'vue-virtual-scroller'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'
import { getScrollbarWidth } from '@app/utils/domHelpers'

export default {
  components: {
    DynamicScroller,
    SfCheckbox,
  },
  props: {
    // The key to use for each row
    keyField: {
      type: String,
      required: true,
    },
    minCellWidth: {
      type: Number,
      default: 0,
    },
    // The items representing the rows of the table
    items: {
      type: Array,
      required: true,
    },
    // The height of each row (needs to be specific for calculations to be accurate)
    itemSize: {
      type: Number,
      required: true,
    },
    rowSize: {
      type: Number,
      required: true,
    },
    sizeDependencies: {
      type: Array,
      required: false,
      default: () => [],
    },
    withSelector: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectAllRows'],
  data() {
    return {
      paddingOffset: 0,
    }
  },
  computed: {
    headerStyle() {
      const selector = this.withSelector ? '25px' : ''

      return {
        padding: `0 ${this.paddingOffset}px 0 0`,
        'grid-template-columns': `${selector} repeat(${this.rowSize}, minmax(${
          this.minCellWidth + 'px'
        }, 1fr)`,
      }
    },
    writableSelect: {
      get() {
        return this.selected
      },
      set(newValue) {
        // Because of Vue's reactivity model and the way sf-checkbox is built, we can just access newValue directly here
        this.$emit('selectAllRows', newValue)
      },
    },
  },
  methods: {
    getPaddingOffset() {
      const element = this.$refs.container

      if (element) {
        const scroller = element.querySelector('.sf-virtual-table__scroller')
        this.paddingOffset = getScrollbarWidth(scroller)
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-virtual-table {
  &__container {
    @include table;

    height: 100%;
    display: grid;
    grid-template:
      'header' auto
      'data' minmax(300px, 5fr)
      / 1fr;
  }

  &__header {
    grid-area: header;
    display: grid;
    border-bottom: 1px solid var(--sf-color-snow-default);
  }

  &__header-cell {
    @include table-header-cell;
  }

  &__scroller {
    grid-area: data;
    overflow-y: auto;
  }

  &__row {
    @include background-color('white');

    &:hover {
      @include background-color('snow', 'light');
    }
  }
}
</style>
